import React, { useState } from "react";
import axios from "axios";
import { Link as Linker } from "react-router-dom";
import { Alert } from "flowbite-react";
import { HiInformationCircle } from "react-icons/hi";
import { HiOutlineMail } from "react-icons/hi";
import { API_BASE_URL } from "../config";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "If an account exists for that email, we sent a reset link."
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    setShowError(false);
    setErrorMessage("");
    setShowSuccess(false);

    const normalized_email = email.trim().toLowerCase();

    if (!normalized_email) {
      setErrorMessage("Please enter your email.");
      setShowError(true);
      return;
    }

    if (!emailRegex.test(normalized_email)) {
      setErrorMessage("Please enter a valid email address.");
      setShowError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/forgot_password`, {
        email: normalized_email,
      });

      const msg =
        res?.data?.message ||
        "If an account exists for that email, we sent a reset link.";

      setSuccessMessage(msg);
      setShowSuccess(true);
    } catch (err) {
      setSuccessMessage("If an account exists for that email, we sent a reset link.");
      setShowSuccess(true);
      console.log("Forgot password error:", err?.response?.data || err?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-[#1896b9]/10 to-white">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="mb-4 text-center">
          <Linker to="/" className="hover:bg-transparent hover:before:w-0 inline-block">
            <div className="text-[#1896b9] font-extrabold text-2xl tracking-tight">
              EduBahamas
            </div>
          </Linker>
          <div className="text-sm text-white mt-1">
            Reset your password securely.
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-6 sm:p-7 shadow-sm ring-1 ring-black/5">
          <div className="mb-5">
            <h1 className="text-gray-900 text-xl font-semibold">
              Forgot your password?
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Enter your email and we’ll send you a reset link.
            </p>
          </div>

          {showError && (
            <Alert
              color="failure"
              icon={HiInformationCircle}
              onDismiss={() => setShowError(false)}
              className="mb-4"
            >
              {errorMessage}
            </Alert>
          )}

          {showSuccess && (
            <Alert
              color="success"
              icon={HiInformationCircle}
              onDismiss={() => setShowSuccess(false)}
              className="mb-4"
            >
              {successMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email address
              </label>

              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <HiOutlineMail className="h-5 w-5" />
                </span>

                <input
                  className={[
                    "w-full rounded-xl bg-gray-50 border px-3 py-3 pl-11 text-gray-900",
                    "placeholder:text-gray-400",
                    "shadow-sm outline-none transition",
                    "focus:bg-white focus:border-[#1896b9] focus:ring-4 focus:ring-[#1896b9]/20",
                    showError ? "border-red-300" : "border-gray-200",
                  ].join(" ")}
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  autoComplete="email"
                  inputMode="email"
                  spellCheck={false}
                  aria-invalid={showError ? "true" : "false"}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setShowError(false);
                    setShowSuccess(false);
                  }}
                />
              </div>

              <p className="mt-2 text-xs text-gray-500">
                If your email exists, you’ll get a link in a few seconds.
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={[
                "w-full rounded-xl py-3 font-semibold text-white",
                "bg-[#1896b9] hover:bg-[#126e87]",
                "shadow-sm transition",
                "focus:outline-none focus:ring-4 focus:ring-[#1896b9]/25",
                "disabled:opacity-60 disabled:cursor-not-allowed",
                "flex items-center justify-center",
              ].join(" ")}
            >
              {isSubmitting && (
                <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white" />
              )}
              {isSubmitting ? "Sending..." : "Send Reset Link"}
            </button>

            <div className="pt-1 text-center">
              <Linker
                to="/signin"
                className="text-sm text-gray-600 hover:text-gray-900 hover:underline hover:bg-transparent hover:before:w-0"
              >
                Back to Sign In
              </Linker>
            </div>
          </form>
        </div>

        <div className="mt-4 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} EduBahamas
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
